import { Button, Input, Form, Select, Switch, Row, Col, InputNumber, Tag } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import TextArea from "antd/es/input/TextArea";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { GoogleMap, LoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { IoLocationOutline } from "react-icons/io5";
import * as Validation from "../../../utils/helper/validation";
import toast from "react-hot-toast";
import { changeLoader } from "../../../redux/reducers/loader";
import * as Apis from "../../../utils/helper/enum";
import globalRequest from "../../../utils/global/globalRequest";
import config from "../../../config";

const mapContainerStyle = {
  height: "400px",
  width: "100%",
};

const libraries = ["places"];

const MachineTab = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const center = {
    lat: config.lat,
    lng: config.lng,
  };
  const [form] = Form.useForm();
  const [markerPosition, setMarkerPosition] = useState(center);
  const [address, setAddress] = useState("");
  const [autocomplete, setAutocomplete] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [sellerList, setSellerList] = useState([]);
  const [doorStatus, setDoorStatus] = useState(false);
  const inputRef = useRef(null);
  const geocoder = useRef(null);
  // const [checked, setChecked] = useState(false);
  let { id } = useParams();

  const handleInputChange = (name) => {
    form.setFields([
      {
        name,
        errors: [],
      },
    ]);
  };

  const onLoadAutocomplete = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      const location = place.geometry.location;
      setAddress(place.formatted_address);
      setMarkerPosition({
        lat: location.lat(),
        lng: location.lng(),
      });
      setInputValue(place.formatted_address);
      form.setFieldValue("address", place.formatted_address);
    }
  };

  const onMarkerDragEnd = (e) => {
    let newLat = e.latLng.lat();
    let newLng = e.latLng.lng();
    setMarkerPosition({
      lat: newLat,
      lng: newLng,
    });
    geocodeLatLng(newLat, newLng);
  };

  const geocodeLatLng = (lat, lng) => {
    if (!geocoder.current) {
      geocoder.current = new window.google.maps.Geocoder();
    }
    const latLng = { lat, lng };
    geocoder.current.geocode({ location: latLng }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          setAddress(results[0].formatted_address);
          setInputValue(results[0].formatted_address);
          form.setFieldValue("address", results[0].formatted_address);
        }
      }
    });
  };

  const getSellerList = async () => {
    try {
      let response = await globalRequest(Apis.MANAGE_CANNED_VENDING_MACHINE.GET_SELLER_LIST, "get", {}, { params: { dropdown: true } }, true);
      if (response?.status === 200) {
        let list = response?.data?.data?.data;
        const options = list.map((item) => ({
          value: item.id.toString(),
          label: item.businessName || item.storeName || item.user?.firstName + " " + item.user?.lastName,
        }));
        setSellerList(options);
        return options;
      }
    } catch (e) {
      toast.error(e?.message);
    }
    return [];
  };

  const handleSubmit = async (values) => {
    dispatch(changeLoader(true));
    try {
      values.lat = markerPosition.lat;
      values.lng = markerPosition.lng;
      // values.address is already in values from the form field
      
      let url = Apis?.MANAGE_CANNED_VENDING_MACHINE?.CREATE;
      let method = "post";
      if (id) {
        url = Apis?.MANAGE_CANNED_VENDING_MACHINE?.EDIT + "/" + id;
        method = "put";
      }
      let response = await globalRequest(url, method, values, {}, true);
      if (response?.status === 200) {
        toast.success(`Machine ${id ? "updated" : "created"} successfully`);
        navigate("/manage-canned-vending-machine");
      } else {
        toast.error(response?.data?.message || "Something went wrong");
      }
    } catch (e) {
      toast.error(e?.message);
    }
    dispatch(changeLoader(false));
  };

  const getDetail = async (id, sellers) => {
    dispatch(changeLoader(true));
    try {
      let response = await globalRequest(Apis.MANAGE_CANNED_VENDING_MACHINE.GET_DETAIL + "/" + id, "get", {}, {}, true);
      if (response?.status === 200) {
        let data = response?.data?.data || response?.data;
        const rawSellerId = data?.seller_id?.toString() || data?.sellerId?.toString();
        // Verify the seller id exists in the loaded options so Select can show the label
        const sellerExists = sellers?.some((s) => s.value === rawSellerId);
        form.setFieldsValue({
          name: data?.name || data?.portalName || data?.localName,
          csnId: data?.csn || data?.csnId || data?.winnsenClientId || data?.SN,
          address: data?.address,
          district: data?.district,
          sellersCount: data?.sellersCount,
          productsCount: data?.productsCount,
          pct: data?.pct,
          status: data?.portalStatus === '1' || data?.status === 'live' ? 'live' : 'offline',
          restocked: data?.restocked,
          x: data?.x,
          y: data?.y,
          seller_id: sellerExists ? rawSellerId : undefined,
        });
        setDoorStatus(Boolean(data?.doorStatus ?? data?.isOpen));
        setAddress(data?.address);
        setInputValue(data?.address);
        if (data?.lat && data?.lng) {
          setMarkerPosition({
            lat: Number(data.lat),
            lng: Number(data.lng),
          });
        }
      }
    } catch (e) {
      toast.error(e?.message);
    }
    dispatch(changeLoader(false));
  };

  useEffect(() => {
    const init = async () => {
      const sellers = await getSellerList();
      if (id) {
        await getDetail(id, sellers);
      } else {
        form.setFieldsValue({
          status: 'offline',
          sellersCount: 0,
          productsCount: 0,
          pct: 0
        });
      }
    };
    init();
  }, [id]);

  return (
    <>
      <div className="min-h-[calc(100vh_-_305px)]">
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          style={{
            maxWidth: 1000,
            width: "100%",
          }}
          autoComplete="off"
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Machine Name"
                name="name"
                rules={[{ required: true, message: "Please enter Machine Name." }]}
              >
                <Input size="large" placeholder="Enter name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Machine ID (CSN)"
                name="csnId"
              >
                <Input size="large" placeholder="Enter Machine ID (CSN)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="District"
                name="district"
              >
                <Input size="large" placeholder="Enter district" />
              </Form.Item>
            </Col>
            {id ? (
              <Col span={12}>
                <Form.Item label="Door Status">
                  <div className="pt-2">
                    {doorStatus ? (
                      <Tag color="orange">Open</Tag>
                    ) : (
                      <Tag color="green">Closed</Tag>
                    )}
                  </div>
                </Form.Item>
              </Col>
            ) : null}
          </Row>

          <Form.Item label="Location Map" name="location_map">
            <div className="responsive-map-container mt-4">
              <LoadScript googleMapsApiKey={config.GOOGLEMAPAPIKEY} libraries={libraries}>
                <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                  <div style={{
                    width: "60%",
                    position: "absolute",
                    zIndex: "1",
                    top: "10px",
                    left: "20px",
                    padding: "10px",
                  }}>
                    <Input
                      placeholder="Search for a location"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                  </div>
                </Autocomplete>
                <GoogleMap mapContainerStyle={mapContainerStyle} center={markerPosition} zoom={12}>
                  <Marker position={markerPosition} draggable={true} onDragEnd={onMarkerDragEnd} />
                </GoogleMap>
              </LoadScript>
            </div>
          </Form.Item>

          <Form.Item
            label="Street Address"
            name="address"
            rules={[{ required: true, message: "Address is required." }]}
          >
            <Input size="large" placeholder="Street address will be filled from map or you can type" />
          </Form.Item>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Sellers Count" name="sellersCount">
                <InputNumber size="large" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Products Count" name="productsCount">
                <InputNumber size="large" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Percentage (pct)" name="pct">
                <InputNumber size="large" style={{ width: '100%' }} min={0} max={100} suffix="%" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item label="Status" name="status">
                <Select size="large">
                  <Select.Option value="live">Live</Select.Option>
                  <Select.Option value="low">Low</Select.Option>
                  <Select.Option value="offline">Offline</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Restocked" name="restocked">
                <Input size="large" placeholder="Restocked status" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="X (%)" name="x">
                <Input size="large" placeholder="e.g. 47%" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Y (%)" name="y">
                <Input size="large" placeholder="e.g. 47%" />
              </Form.Item>
            </Col>
          </Row>

          {/* <Row gutter={24}>
            <Col span={24}>
              <Form.Item 
                label="Assign Seller" 
                name="seller_id" 
              >
                <Select
                  showSearch
                  size="large"
                  placeholder="Select Seller"
                  options={sellerList}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row> */}

          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={() => navigate("/manage-canned-vending-machine")}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save changes
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};

export default MachineTab;
